import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '@/shared/lib';
import { Spinner } from './Spinner';

/**
 * Кнопка по README хендоффа (§3, §5, «Оверлеи»):
 *  solid  — фон wine-ink, текст ink-inverse; hover — wine-bordeaux (создание, «Сохранить», окна);
 *  ghost  — прозрачная, кольцо inset 1px line-strong, ink-900 (действия карточки, «Отмена»);
 *  outline— белая, рамка line-strong; hover — surface-sunken («Экспорт», «Сбросить фильтры»);
 *  danger — прозрачная, текст и кольцо status-error;
 *  gold   — фон gold-wash, ink-900.
 *  Действия карточки: hover — opacity .86 (README §5).
 * Высоты: xs 26 (правка раздела), sm 28 (r14), md 32 (r16), lg 38 (r19, 13/600 — справочники).
 */
export const buttonVariants = cva(
  [
    'inline-flex shrink-0 cursor-pointer items-center justify-center gap-6 whitespace-nowrap font-ui',
    'transition-[background-color,opacity,box-shadow] duration-(--duration-fast) ease-(--ease-standard)',
    'disabled:cursor-not-allowed disabled:opacity-50 aria-busy:cursor-progress',
  ],
  {
    variants: {
      variant: {
        solid: 'bg-wine-ink text-ink-inverse enabled:hover:bg-wine-bordeaux',
        ghost:
          'bg-transparent text-ink-900 shadow-[inset_0_0_0_1px_var(--vw-line-strong)] enabled:hover:opacity-86',
        outline:
          'border border-line-strong bg-paper-pure text-ink-900 enabled:hover:bg-surface-sunken',
        danger:
          'bg-transparent text-status-error shadow-[inset_0_0_0_1px_var(--vw-status-error)] enabled:hover:opacity-86',
        gold: 'bg-gold-wash text-ink-900 enabled:hover:opacity-86',
        link: 'h-auto bg-transparent p-0 text-wine-bordeaux enabled:hover:text-accent-deep',
      },
      size: {
        xs: 'h-26 rounded-16 px-10 text-[12px] font-medium',
        sm: 'h-28 rounded-14 px-12 text-[12px] font-medium',
        md: 'h-32 rounded-16 px-12 text-[13px] font-medium',
        lg: 'h-38 rounded-19 px-16 text-[13px] font-semibold',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0 text-[12px] font-medium' }],
    defaultVariants: { variant: 'solid', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Отрисовать дочерний элемент (например, ссылку) со стилями кнопки. */
  asChild?: boolean;
  /** Ожидание ответа: кнопка неактивна, показывается индикатор. */
  loading?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  type,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}
